import {Box, SxProps, Theme} from '@mui/material';
import {useCanAll} from 'src/permissions/hooks';
import {EngineeringOutlined} from '@mui/icons-material';
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {usersApi} from 'src/api';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {store} from 'src/app/store';
import AMPModal from 'src/components/common/AMPModal';
import {ManagersForm} from 'src/components/forms/ManagersForm';
import {UserStatsStrip} from 'src/components/users/UserStatsStrip';
import {TechnicalTeamTable} from 'src/components/technicalTeam/TechnicalTeamTable';
import {UsersSortFieldEnum} from 'src/enums/users';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {DataType, ListParamsType} from 'src/types';
import {useListQueryState} from 'src/hooks/useListQueryState';
import {UsersSearchRequestData} from 'src/types/users';
import {UserTypeEnum} from 'src/enums/common';
import {managerTypes} from 'src/utils/consts';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening one manager and coming back restores the view instead of resetting it.
 *
 * `userType` is one value where the request carries a list: the tile and the toolbar select are both
 * single-choice, so a list of one is all this screen can produce.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    userType: '',
    notActivated: false,
    sortField: UsersSortFieldEnum.EMAIL as string,
    sortAsc: true,
};

/**
 * The block and the list are two cards on one page. The top margin is the app bar's: the table used
 * to be first and carried its own, and now that the block is above it the clearance lives here.
 */
const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

type UsersQueryTrigger = ReturnType<typeof usersApi.useLazyGetUsersQuery>[0];

const handleGetManagers =
    (trigger: UsersQueryTrigger, businessId?: number) =>
        async (tableData: UsersSearchRequestData, params: { page: number; size: number }) => {
            store.dispatch(preloaderChangeStatus(true));

            const newData: DataType = {
                ...tableData,
                businessId: tableData.businessId ? tableData.businessId : businessId,
                userTypes: tableData.userTypes ? tableData.userTypes : managerTypes,
            };

            Object.keys(tableData).map((item: string) => {
                !newData[item] && delete newData[item];
            });

            const data: UsersSearchRequestData = {
                ...newData,
            };

            try {
                return await trigger({data, params}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

export const TechnicalTeam: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'createNewManager'});
    const {authInfo} = useAppSelector((state) => state.common);
    const [trigger, response] = usersApi.useLazyGetUsersQuery();
    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);

    // A boolean rather than `authInfo` itself: the effect below waits for the session to arrive, and
    // depending on the object would re-ask the server every time anything in it was replaced.
    const isSignedIn = Boolean(Object.keys(authInfo).length);

    // Both derived from the address bar rather than kept beside it.
    const filterParams: UsersSearchRequestData = useMemo(() => ({
        keyword: query.keyword,
        businessId: 0,
        userTypes: query.userType ? [query.userType as UserTypeEnum] : undefined,
        notActivated: query.notActivated || undefined,
        sortAsc: query.sortAsc,
        sortField: query.sortField,
    }), [query]);

    const tableParams: ListParamsType = useMemo(
        () => ({page: query.page, size: query.size}),
        [query.page, query.size]
    );

    const handleOpenModal = () => {
        setOpen(true);
    };
    const handleCloseModal = () => {
        setOpen(false);
        callHandler(filterParams, tableParams);
    };

    // The same summary the users page reads, asked with the manager types this list shows. Two of
    // its three gaps are meaningless here — managers hold no contracts — so only the first is shown.
    const {data: managerStats} = usersApi.useGetUserStatsQuery({
        data: {
            ...filterParams,
            businessId: filterParams.businessId || businessId,
            userTypes: managerTypes,
            notActivated: undefined,
        },
    }, {skip: !Object.keys(authInfo).length});

    // Type and gap are alternatives, not filters to combine: each selection clears the other, and
    // clicking the active one clears it.
    const handleSelectType = (type: UserTypeEnum) => {
        setQuery({
            ...query,
            page: 0,
            notActivated: false,
            userType: query.userType === type ? '' : type,
        });
    };

    const handleSelectGap = () => {
        setQuery({
            ...query,
            page: 0,
            userType: '',
            notActivated: !query.notActivated,
        });
    };

    /**
     * Whatever the list is now asking for, as a whole request body from the table below.
     *
     * Back to the first page, because the row that was on page four of the old filter is not on page
     * four of the new one.
     */
    const handleChange = (value: UsersSearchRequestData) => {
        setQuery({
            ...query,
            page: 0,
            keyword: value.keyword ?? '',
            userType: value.userTypes?.length === 1 ? String(value.userTypes[0]) : '',
            notActivated: value.notActivated ?? false,
            sortField: value.sortField ?? UsersSortFieldEnum.EMAIL,
            sortAsc: value.sortAsc ?? true,
        });
    };

    const handleChangePage = (page: number) => setQuery({...query, page});

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => setQuery({
        ...query,
        page: 0,
        size: +e.target.value || DEFAULT_PAGE_SIZE,
    });

    // Memoised so the effect below can depend on it: `trigger` is stable and the business only
    // changes when a platform administrator switches tenant, which has to re-ask anyway.
    const callHandler = useMemo(() => handleGetManagers(trigger, businessId), [trigger, businessId]);

    // POST /users/managers asks for both, with allOf = true. Offering the button on users.create
    // alone showed it to anyone who may create a driver and got them a 403 on submit.
    const canCreateManager = useCanAll('users.create', 'accessControl.manageBindings');

    /**
     * One place asks the server, whenever the address bar says something else.
     *
     * Every handler above only writes the URL. Before this each of them called the server itself and
     * a mount effect had to put the state back together from `location.state` and a regex over
     * `location.search` - so arriving with a filter already in the URL, which is what Back and a
     * reload both are, fetched the unfiltered list.
     */
    useEffect(() => {
        if (!isSignedIn) {
            return;
        }
        void callHandler(filterParams, tableParams);
    }, [isSignedIn, callHandler, filterParams, tableParams]);

    return (
        <Box sx={pageSx}>
            <UserStatsStrip
                data={managerStats}
                title="technicalTeam"
                types={managerTypes}
                gaps={['notActivated']}
                activeType={
                    filterParams.userTypes?.length === 1 ? filterParams.userTypes[0] : undefined
                }
                onSelectType={handleSelectType}
                activeGap={filterParams.notActivated ? 'notActivated' : undefined}
                onSelectGap={handleSelectGap}
            />
            {response.data && (
                <TechnicalTeamTable
                    data={response.data}
                    filters={filterParams}
                    pageNumber={tableParams.page}
                    handleUpdateTableData={handleChange}
                    handleChangePage={handleChangePage}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    size={tableParams.size}
                    title={intl.formatMessage({id: 'team'})}
                    onAddClick={handleOpenModal}
                    buttonText="addManager"
                    onlyTitle={!canCreateManager}
                    icon={<EngineeringOutlined/>}
                />
            )}
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <ManagersForm
                    handleClose={handleCloseModal}
                    businessId={businessId}
                />
            </AMPModal>
        </Box>
    );
};
